import React, { useState, useEffect } from 'react';
import { 
  TrophyIcon, 
  UserGroupIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { TrophyIcon as TrophySolidIcon } from '@heroicons/react/24/solid';
import intranetApi from '../../services/intranetApi';

const CompanyLeaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState({
    company: [],
    department: []
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('company');
  const [timeframe, setTimeframe] = useState('all_time');

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchLeaderboard();
    }
  }, [timeframe, currentUser]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Always fetch company leaderboard
      const companyResponse = await intranetApi.points.getLeaderboard({ 
        scope: 'company', 
        timeframe 
      });

      let departmentResponse = null;
      
      // Only fetch department leaderboard if user has a department
      if (currentUser && currentUser.department_id) {
        departmentResponse = await intranetApi.points.getLeaderboard({ 
          scope: 'department', 
          timeframe,
          department_id: currentUser.department_id
        });
      }

      setLeaderboardData({
        company: companyResponse.data.leaderboard || [],
        department: departmentResponse ? (departmentResponse.data.leaderboard || []) : []
      });
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await intranetApi.points.getCurrentUserPoints();
      setCurrentUser(response.data.user_points);
    } catch (err) {
      console.error('Error fetching current user points:', err);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <TrophySolidIcon className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <TrophySolidIcon className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <TrophySolidIcon className="h-6 w-6 text-amber-600" />;
    return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800';
    if (rank === 2) return 'bg-gray-100 text-gray-800';
    if (rank === 3) return 'bg-amber-100 text-amber-800';
    return 'bg-blue-100 text-blue-800';
  };

  const formatPoints = (points) => {
    return points?.toLocaleString() || '0';
  };

  const renderLeaderboardTable = (data, title, icon) => {
    if (loading) {
      return (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            {icon}
            <h3 className="text-lg font-medium text-gray-900 ml-3">{title}</h3>
          </div>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/6"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
          <div className="flex items-center justify-center mb-4">
            {icon}
            <h3 className="text-lg font-medium text-gray-900 ml-3">{title}</h3>
          </div>
          <p className="text-gray-500">No ranking data available yet.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            {icon}
            <h3 className="text-lg font-medium text-gray-900 ml-3">{title}</h3>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {data.map((user, index) => {
            const isCurrentUser = currentUser && user.user_id === currentUser.user_id;
            return (
              <div 
                key={user.user_id} 
                className={`p-6 flex items-center justify-between ${
                  isCurrentUser ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 w-12 flex justify-center">
                    {getRankIcon(user.rank)}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                      {user.user_name ? user.user_name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isCurrentUser ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {user.user_name || 'Unknown User'}
                      {isCurrentUser && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          You
                        </span>
                      )}
                    </p>
                    {user.department_name && (
                      <p className="text-xs text-gray-500">{user.department_name}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-semibold ${isCurrentUser ? 'text-indigo-900' : 'text-gray-900'}`}>
                    {formatPoints(user.total_points)} pts
                  </p>
                  {user.monthly_points && (
                    <p className="text-xs text-gray-500">
                      +{formatPoints(user.monthly_points)} this month
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Company Leaderboard</h2>
            <p className="mt-1 text-sm text-gray-500">
              View employee engagement points and rankings
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchLeaderboard} 
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Company Leaderboard</h2>
          <p className="mt-1 text-sm text-gray-500">
            View employee engagement points and rankings
          </p>
        </div>
        
        {/* Timeframe Filter */}
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all_time">All Time</option>
          <option value="monthly">This Month</option>
          <option value="weekly">This Week</option>
        </select>
      </div>

      {/* Current User Stats */}
      {currentUser && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">Your Performance</h3>
              <p className="text-indigo-100">Keep up the great work!</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{formatPoints(currentUser.total_points)}</p>
              <p className="text-indigo-100">Total Points</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-2xl font-semibold">
                #{currentUser.company_rank?.rank || 'N/A'}
              </p>
              <p className="text-indigo-100 text-sm">Company Rank</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <p className="text-2xl font-semibold">
                #{currentUser.department_rank?.rank || 'N/A'}
              </p>
              <p className="text-indigo-100 text-sm">Department Rank</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('company')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'company'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BuildingOfficeIcon className="h-5 w-5 inline mr-2" />
            Company-wide
          </button>
          <button
            onClick={() => setActiveTab('department')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'department'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserGroupIcon className="h-5 w-5 inline mr-2" />
            Department
          </button>
        </nav>
      </div>

      {/* Leaderboard Content */}
      <div className="grid grid-cols-1 gap-6">
        {activeTab === 'company' && renderLeaderboardTable(
          leaderboardData.company,
          'Company Rankings',
          <BuildingOfficeIcon className="h-6 w-6 text-indigo-600" />
        )}
        {activeTab === 'department' && renderLeaderboardTable(
          leaderboardData.department,
          'Department Rankings',
          <UserGroupIcon className="h-6 w-6 text-green-600" />
        )}
      </div>

      {/* Points Legend */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2" />
          How Points Are Earned
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <StarIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Creating Posts</p>
              <p className="text-gray-500">Share company updates</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <StarIcon className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Commenting</p>
              <p className="text-gray-500">Engage in discussions</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
              <StarIcon className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Event Attendance</p>
              <p className="text-gray-500">Participate in events</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <StarIcon className="h-4 w-4 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Best Answer</p>
              <p className="text-gray-500">Provide helpful solutions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyLeaderboard; 